import { useState } from 'react';
import { Loader2, Copy, AlertTriangle, CheckCircle2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(v) {
  if (v == null) return '--';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? 'border-red-300 dark:border-red-800' : ''}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${warn ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function VerdictBanner({ verdict, detail }) {
  if (!verdict) return null;
  const isGood = verdict === 'CORRECTLY-CANCELLED' || verdict === 'LEGITIMATE-LIVE' || verdict === 'NOT-FOUND';
  const isBad = verdict === 'CANCELLED-AS-LIVE' || verdict === 'DUPLICATE';

  let icon, bg;
  if (isGood) {
    icon = <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />;
    bg = 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800';
  } else if (isBad) {
    icon = <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />;
    bg = 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
  } else {
    icon = <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
    bg = 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
  }

  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 ${bg}`}>
      {icon}
      <div className="space-y-1 text-sm">
        <p className="font-medium">Yerim Lee verdict: <span className="font-mono">{verdict}</span></p>
        {detail && <p className="text-muted-foreground">{detail}</p>}
      </div>
    </div>
  );
}

function ClusterTable({ clusters }) {
  if (!clusters?.length) return <p className="text-sm text-muted-foreground">No duplicate clusters found.</p>;
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-2 font-medium">External ID</th>
            <th className="text-left px-3 py-2 font-medium">Guest</th>
            <th className="text-left px-3 py-2 font-medium">Property</th>
            <th className="text-center px-3 py-2 font-medium">Count</th>
            <th className="text-left px-3 py-2 font-medium">Type</th>
            <th className="text-left px-3 py-2 font-medium">Uplisting IDs</th>
            <th className="text-left px-3 py-2 font-medium">Revenues</th>
            <th className="text-left px-3 py-2 font-medium">Statuses</th>
          </tr>
        </thead>
        <tbody>
          {clusters.map((c) => (
            <tr key={c.external_reservation_id} className="border-b last:border-0">
              <td className="px-3 py-2 font-mono text-xs">{c.external_reservation_id}</td>
              <td className="px-3 py-2 text-xs">{c.guest_name}</td>
              <td className="px-3 py-2 text-xs">{c.property_name}</td>
              <td className="px-3 py-2 text-center tabular-nums">{c.count}</td>
              <td className="px-3 py-2">
                <Badge variant={c.is_modification ? 'default' : 'secondary'}>
                  {c.is_modification ? 'modification' : 'identical'}
                </Badge>
              </td>
              <td className="px-3 py-2 font-mono text-xs">{c.uplisting_ids?.join(', ')}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{c.revenues?.map(r => fmt(r)).join(', ')}</td>
              <td className="px-3 py-2 text-xs">{c.statuses?.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function UplistingDuplicatesPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.uplistingDuplicatesAndCancellations();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const dup = results?.duplicate_clusters;
  const yerim = results?.yerim_lee;
  const hasProblems = (dup && parseFloat(dup.double_counted_revenue_eur) > 0)
    || (yerim && yerim.verdict === 'CANCELLED-AS-LIVE');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Copy className="h-5 w-5 text-orange-500" />
          Uplisting Duplicates &amp; Cancellations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Finds bookings stored under multiple uplisting_ids for one
          external_reservation_id (double-counting from Uplisting re-IDing
          modified bookings), checks whether cancelled bookings are stored
          as live, and counts re-ingested records with stale quarantine metadata.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {results && (
          <div className="space-y-6">
            {/* Top-level banner */}
            {hasProblems && (
              <div className="flex items-start gap-3 rounded-lg border p-4 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                <p className="text-sm font-medium">
                  {parseFloat(dup?.double_counted_revenue_eur) > 0 &&
                    `Double-counted revenue: \u20AC${fmt(dup.double_counted_revenue_eur)}. `}
                  {yerim?.verdict === 'CANCELLED-AS-LIVE' &&
                    'Yerim Lee booking stored as live but reportedly cancelled.'}
                </p>
              </div>
            )}

            {/* Section 1: Duplicate clusters */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Duplicate Clusters</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Clusters" value={dup?.cluster_count ?? 0} warn={dup?.cluster_count > 0} />
                <Stat
                  label="Double-counted revenue"
                  value={dup ? `\u20AC${fmt(dup.double_counted_revenue_eur)}` : '--'}
                  warn={parseFloat(dup?.double_counted_revenue_eur) > 0}
                />
                <Stat label="Modifications" value={dup?.modification_clusters ?? 0} />
                <Stat label="Identical" value={dup?.identical_clusters ?? 0} />
              </div>
              <ClusterTable clusters={dup?.sample} />
            </div>

            {/* Section 2: Yerim Lee */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Yerim Lee Check</h3>
              <VerdictBanner
                verdict={yerim?.verdict}
                detail={
                  yerim?.verdict === 'CANCELLED-AS-LIVE'
                    ? `Stored status: ${yerim.stored_statuses?.join(', ')}. Payload status: ${yerim.payload_status_field ?? 'none'}. Payload cancelled_at: ${yerim.payload_cancelled_at ?? 'none'}. Webhook events: ${yerim.webhook_events?.join(', ') || 'none'}.`
                    : yerim?.verdict === 'DUPLICATE'
                    ? `Found under external_reservation_ids: ${yerim.external_reservation_ids?.join(', ')}. Uplisting IDs: ${yerim.uplisting_ids?.join(', ')}.`
                    : yerim?.verdict === 'NOT-FOUND'
                    ? 'No matching record found in uplisting_reservations.'
                    : `Stored status: ${yerim?.stored_statuses?.join(', ')}.`
                }
              />
              {yerim?.records?.length > 0 && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-left px-3 py-2 font-medium">Check-in</th>
                        <th className="text-left px-3 py-2 font-medium">Check-out</th>
                        <th className="text-right px-3 py-2 font-medium">Nights</th>
                        <th className="text-right px-3 py-2 font-medium">Net Revenue</th>
                        <th className="text-left px-3 py-2 font-medium">Channel</th>
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yerim.records.map((r, i) => (
                        <tr key={r.uplisting_id || i} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id}</td>
                          <td className="px-3 py-2">
                            <Badge variant={r.status === 'cancelled' ? 'destructive' : 'secondary'}>{r.status}</Badge>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.check_out}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.nights}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(r.net_revenue)}</td>
                          <td className="px-3 py-2 text-xs">{r.channel}</td>
                          <td className="px-3 py-2 text-xs">{r.property_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 3: Stale quarantine metadata */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Stale Quarantine Metadata</h3>
              <Stat
                label="Records with ingestion_status=ok but quarantine fields present"
                value={results.stale_quarantine_metadata_count ?? 0}
                warn={results.stale_quarantine_metadata_count > 0}
              />
            </div>

            {/* Section 4: Cancellation handling */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Uplisting Cancellation Handling</h3>
              <div className="flex items-center gap-2">
                <Badge variant={results.uplisting_cancellation_handling === 'partial' ? 'outline' : 'default'}>
                  {results.uplisting_cancellation_handling}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{results.uplisting_cancellation_detail}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
